export type SalonInputData = {
  id: string;
  year: string;
  quarter: string;
  salonDetailPage: string; //salon detail -> nid
  title: string;
  tnSpBeginning: number;
  tnSpEnd: number;
  tnColorTransactions: number;
  tnExistGuestsReturning: number;
  tnExistGuestsServiced: number;
  tnHairTransactions: number;
  tnNewGuestsReturning: number;
  tnNewGuestsServiced: number;
  tnPrebookedAppointments: number;
  tnRetailOnlyTr: number;
  tnServiceTrWithRetail: number;
  tnServiceTransactions: number;
  tnTeamMembersThisYear: number;
  tnTreatmentTransactions: number;
  totalRetailSales: number;
  totalServiceSales: number;
  kpi: Record<string, number>;
};

export interface ISalonInputFormItem {
  label: string;
  id: string;
  required?: boolean;
  frequency?: number;
}

import { KpiTitle } from './SalonKpiData';
// maps KPI to its input validation type
export const getKPIType = (kpi: KpiTitle): 'percentage' | 'money' | 'integer' => {
  switch (kpi) {
    case KpiTitle.existGuestRetention:
    case KpiTitle.newGuestRetention:
    case KpiTitle.spRetention:
    case KpiTitle.pColorTransactions:
    case KpiTitle.pGuestsPrebooked:
    case KpiTitle.pGuestsPurchasing:
    case KpiTitle.treatmentTransactions:
    case KpiTitle.pRetailSales:
      return 'percentage';
    case KpiTitle.rpct:
    case KpiTitle.rprt:
      return 'money';
    //case KpiTitle.tnTransactionsBmrk: return 'integer';
    default:
      return 'integer';
  }
};

export const kpiTitleToDbKey: Record<KpiTitle, string> = {
  [KpiTitle.existGuestRetention]: 'kpi_exist_guest_retention',
  [KpiTitle.newGuestRetention]: 'kpi_new_guest_retention',
  [KpiTitle.spRetention]: 'kpi_sp_retention',
  [KpiTitle.pColorTransactions]: 'kpi_p_color_transactions',
  [KpiTitle.pGuestsPrebooked]: 'kpi_p_guests_prebooked',
  [KpiTitle.pGuestsPurchasing]: 'kpi_p_guests_purchasing',
  [KpiTitle.treatmentTransactions]: 'kpi_treatment_transactions',
  [KpiTitle.pRetailSales]: 'kpi_p_retail_sales',
  [KpiTitle.rpct]: 'kpi_rpct',
  [KpiTitle.rprt]: 'kpi_rprt',
  [KpiTitle.tnTransactionsBmrk]: 'kpi_tn_transactions',
};

// Proper mapping from DB key to KpiTitle
const dbKeyToKpiTitle: Record<string, KpiTitle> = {
  kpiExistGuestRetention: KpiTitle.existGuestRetention,
  kpiNewGuestRetention: KpiTitle.newGuestRetention,
  kpiSpRetention: KpiTitle.spRetention,
  kpiPColorTransactions: KpiTitle.pColorTransactions,
  kpiPGuestsPrebooked: KpiTitle.pGuestsPrebooked,
  kpiPGuestsPurchasing: KpiTitle.pGuestsPurchasing,
  kpiTreatmentTransactions: KpiTitle.treatmentTransactions,
  kpiPRetailSales: KpiTitle.pRetailSales,
  kpiRpct: KpiTitle.rpct,
  kpiRprt: KpiTitle.rprt,
  kpiTnTransactions: KpiTitle.tnTransactionsBmrk,
};

// Correct function implementation
export const getKpiTitleForDbKey = (dbKey: string): KpiTitle | undefined => {
  return dbKeyToKpiTitle[dbKey];
};

// maps KPIs to their calculation values
export const selectableKPIs: Record<KpiTitle, string[]> = {
  [KpiTitle.existGuestRetention]: ['tnExistGuestsReturning', 'tnExistGuestsServiced'],
  [KpiTitle.newGuestRetention]: ['tnNewGuestsReturning', 'tnNewGuestsServiced'],
  [KpiTitle.pColorTransactions]: ['tnColorTransactions', 'tnHairTransactions'],
  [KpiTitle.pGuestsPrebooked]: ['tnPrebookedAppointments'],
  [KpiTitle.pGuestsPurchasing]: ['tnServiceTrWithRetail'],
  [KpiTitle.rpct]: [],
  [KpiTitle.treatmentTransactions]: ['tnTreatmentTransactions', 'tnHairTransactions'],
  [KpiTitle.tnTransactionsBmrk]: [],
  [KpiTitle.spRetention]: ['tnSpBeginning', 'tnSpEnd'],
  [KpiTitle.rprt]: ['tnServiceTrWithRetail'],
  [KpiTitle.pRetailSales]: [],
};

// 'frequency' represents how many KPIs the calculation is used in
export const SalonInputFormMap = {
  staff: [
    {
      label: '# of service providers (at the beginning of the quarter)',
      id: 'tnSpBeginning',
      required: true,
      frequency: 1,
    },
    {
      label: '# of same service providers remaining (at the end of the quarter)',
      id: 'tnSpEnd',
      required: true,
      frequency: 1,
    },
    {
      label: '# of team members (this quarter)',
      id: 'tnTeamMembersThisYear',
      frequency: 1,
    },
  ],
  guest: [
    {
      label: 'total # new guests serviced',
      id: 'tnNewGuestsServiced',
      frequency: 1,
    },
    {
      label: 'total # existing guests serviced',
      id: 'tnExistGuestsServiced',
      frequency: 1,
    },
    {
      label: 'total # new service guests returning within 3 months',
      id: 'tnNewGuestsReturning',
      frequency: 1,
    },
    {
      label: 'total # existing service guests returning within 3 months',
      id: 'tnExistGuestsReturning',
      frequency: 1,
    },
    {
      label: '# pre-booked appointments',
      id: 'tnPrebookedAppointments',
      frequency: 1,
    },
  ],
  service: [
    {
      label: 'total service sales',
      id: 'totalServiceSales',
      frequency: 1,
    },
    {
      label: 'total # service transactions',
      id: 'tnServiceTransactions',
      frequency: 4,
    },
    {
      label: 'total # color transactions',
      id: 'tnColorTransactions',
      frequency: 1,
    },
    {
      label: 'total # hair service transactions',
      id: 'tnHairTransactions',
      frequency: 2,
    },
    {
      label: '# service transactions with retail',
      id: 'tnServiceTrWithRetail',
      frequency: 2,
    },
    {
      label: 'total # of treatment transactions',
      id: 'tnTreatmentTransactions',
      frequency: 1,
    },
  ],
  retail: [
    {
      label: 'total retail sales',
      id: 'totalRetailSales',
      frequency: 3,
    },
    {
      label: '# retail only transactions',
      id: 'tnRetailOnlyTr',
      frequency: 3,
    },
  ],
};

export enum SalonInputTitleDescription {
  staff = "These inputs fuel the KPI's about staff",
  guest = "These inputs fuel the KPI's about guests",
  service = "These inputs fuel the KPI's about services",
  retail = "These inputs fuel the KPI's about retail",
  transactions = "These inputs fuel the KPI's about transactions",
}
